import React, { useState } from 'react';
import {
    Avatar,
    Box,
    Button,
    Container,
    Stack,
    Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import { useFetch } from '../../hooks/useFetch';
import LoadingContent from '../../components/LoadingContent';
import useHandleErrors from '../../hooks/useHandleErrors';
import server from '../../global/server';

/**
 * Anfitriões que o conviva acompanha (Termos 1.12).
 *
 * A cláusula garante deixar de acompanhar "a qualquer momento, diretamente pela
 * Plataforma". Sem esta tela isso dependia de lembrar o perfil de cada um e
 * visitá-lo, então é aqui que o "a qualquer momento" acontece de verdade.
 */
const Acompanhando = () => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { data, error, isLoading, mutate } = useFetch('/acompanhando');
    const [errs, setErrs] = useState();
    const [saindoDe, setSaindoDe] = useState(null);
    useHandleErrors(errs || error);

    const deixarDeAcompanhar = async username => {
        try {
            setSaindoDe(username);
            const { data: resposta } = await server.delete(`/host/${username}/acompanhar`);
            enqueueSnackbar(resposta.message, { variant: 'success' });
            mutate();
        } catch (erro) {
            setErrs(erro);
        } finally {
            setSaindoDe(null);
        }
    };

    if (isLoading) return <LoadingContent />;

    return (
        <Container maxWidth="sm" sx={{ py: 3 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
                Anfitriões que você acompanha
            </Typography>

            {!data?.length ? (
                <Stack alignItems="center" spacing={2} sx={{ py: 6, textAlign: 'center' }}>
                    <NotificationsNoneOutlinedIcon sx={{ fontSize: 56, color: '#bdbdbd' }} />
                    <Typography variant="body2" color="text.secondary">
                        Você ainda não acompanha ninguém. Ao acompanhar um anfitrião, você recebe um aviso
                        sempre que ele criar um encontro, com o valor e os horários.
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => navigate('/lista')}
                        sx={{
                            bgcolor: '#006a61',
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: '8px',
                            '&:hover': { bgcolor: '#005a52' },
                        }}
                    >
                        Descobrir anfitriões
                    </Button>
                </Stack>
            ) : (
                <Stack spacing={1.5} sx={{ mt: 2 }}>
                    {data.map(anfitriao => (
                        <Stack
                            key={anfitriao.username}
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                            sx={{
                                border: '1px solid #eee',
                                borderRadius: '12px',
                                p: 1.5,
                            }}
                        >
                            <Avatar
                                src={anfitriao.profileImage}
                                sx={{ width: 48, height: 48, cursor: 'pointer' }}
                                onClick={() => navigate(`/anfitriao/${anfitriao.username}`)}
                            />

                            <Box
                                sx={{ flexGrow: 1, minWidth: 0, cursor: 'pointer' }}
                                onClick={() => navigate(`/anfitriao/${anfitriao.username}`)}
                            >
                                <Typography variant="subtitle2" fontWeight={600} noWrap>
                                    {anfitriao.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap display="block">
                                    @{anfitriao.username}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {anfitriao.encontros_abertos === 0
                                        ? 'Nenhum encontro em aberto'
                                        : `${anfitriao.encontros_abertos} encontro${anfitriao.encontros_abertos > 1 ? 's' : ''} em aberto`}
                                </Typography>
                            </Box>

                            <LoadingButton
                                size="small"
                                loading={saindoDe === anfitriao.username}
                                onClick={() => deixarDeAcompanhar(anfitriao.username)}
                                sx={{ textTransform: 'none', color: '#c62828', flexShrink: 0 }}
                            >
                                Deixar de seguir
                            </LoadingButton>
                        </Stack>
                    ))}
                </Stack>
            )}
        </Container>
    );
};

export default Acompanhando;
